import { AccountBalanceCard } from "@/components/shared/cards/account-balance-card";
import {
  TableFilters,
  useTableInstanceContext,
} from "@/components/shared/data-table";
import FiltersRoot from "@/components/shared/data-table/filters";
import { GlobalFilterInput } from "@/components/shared/data-table/filters/inputs/global-filter";
import { DatePickerWithRangeInput } from "@/components/shared/form/data-picker-with-range-input";
import { Button } from "@/components/ui/button";
import type { RawFilters } from "@/hooks/useQueryFilters";
import { formatAmount } from "@/lib/utils";
import { useTenant } from "@/providers/session";
import type { GetAllInvoiceInvoice } from "@/queries/invoices/get-all";
import { useNavigate } from "@tanstack/react-router";
import type { Table } from "@tanstack/react-table";
import { CalendarClock, SendHorizonal } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getPaymentAmountToPay } from "./get-payments-amount";

export enum BankTransfersStatusFilters {
  TO_PAY = "to_pay",
  PAID = "paid",
}

type Props = {
  tab: BankTransfersStatusFilters;
  filters: RawFilters<any>;
  setFilters: (filters: RawFilters<any>) => void;
  globalSearch: string;
  setGlobalSearch: (globalSearch: string) => void;
  table: Table<GetAllInvoiceInvoice>;
};

export const BankTransfersFilters = ({
  tab,
  filters,
  setFilters,
  globalSearch,
  setGlobalSearch,
  table,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tenant = useTenant();

  const totalAmount = useMemo(() => {
    const filteredRows = table.getFilteredRowModel().rows;
    return filteredRows.reduce((sum, row) => {
      const paymentAmount = getPaymentAmountToPay(row.original.draftPayments);
      if (paymentAmount) {
        return sum + paymentAmount;
      }
      return sum;
    }, 0);
  }, [table.getFilteredRowModel().rows]);

  const currency = useMemo(() => {
    const firstRow = table.getFilteredRowModel().rows[0];
    return firstRow?.original?.invoice?.currency || "EUR";
  }, [table.getFilteredRowModel().rows]);

  return (
    <div>
      <div className="flex flex-row gap-4 py-2">
        <div className="w-full flex items-center justify-between">
          <div className="flex flex-row gap-2">
            <div className="w-96">
              <GlobalFilterInput
                placeholder={t("bank-transfers.search")}
                value={globalSearch}
                onChange={setGlobalSearch}
              />
            </div>
            <FiltersRoot onSubmit={setFilters} values={filters}>
              <DatePickerWithRangeInput
                name="dueDate"
                placeholder={t("bank-transfers.date")}
                Icon={CalendarClock}
              />
            </FiltersRoot>
          </div>
          <div className="flex items-center gap-4">
            <AccountBalanceCard />
            {tab === BankTransfersStatusFilters.TO_PAY && (
              <Button onClick={handleTransferAll} disabled={totalAmount === 0}>
                <SendHorizonal className="w-4 h-4 mr-2" />
                Alle überweisen ({formatAmount(totalAmount, currency)})
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  function handleTransferAll() {
    const filteredRows = table.getFilteredRowModel().rows;

    const invoiceIds = filteredRows.map((row) => row.original.invoiceDetailsId);

    navigate({
      to: "/$company/unternehmen/bezahlen",
      params: { company: tenant.companySlug },
      search: {
        invoiceIds,
        navigateBackTo: "ueberweisungen",
      },
    });
  }
};
